import {
  useRetryTask,
  useTask,
  useUpdateResult,
  useUpdateRunStatus,
} from "@/api/workflow-hooks";
import { Button } from "@/components/button";
import { DocumentViewer } from "@/components/document-viewer";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/tabs";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/tooltip";
import { generateMergedPDF } from "@/lib/merge-pdf";
import { cn } from "@/lib/utils";
import { tiptapToPlaintext } from "@/pages/Projects/Editor/TextEditor";
import { QuestionMarkCircledIcon } from "@radix-ui/react-icons";
import type { CitationSelect, TaskDocumentSelect } from "@repo/db/schema";
import {
  ACTION_GROUP_NODE_NAME,
  DOCUMENT_GROUP_NODE_NAME,
  RULE_GROUP_HOLDER_NODE_NAME,
  TRIGGER_GROUP_NODE_NAME,
} from "@repo/shared/migration-types";
import { DocumentType } from "@repo/shared/workflow";
import type {
  ActionNode,
  DocumentNode,
  EmailNode,
  EmailResult,
  Model,
  ObjectModel,
} from "@repo/shared/workflow";
import {
  type NodeData,
  ResultStatus,
  RunStatus,
  isDocumentNode,
  isDocumentResult,
  isEmailNode,
  isEmailResult,
  isGroupNode,
  isSendEmailActionNode,
} from "@repo/shared/workflow";
import type {
  DocumentResult,
  ExtractionNode,
  ExtractionResult,
  ModelResult,
  ResultOutput,
  Run,
} from "@repo/shared/workflow";
import { produce } from "immer";
import _ from "lodash";
import {
  Check,
  ChevronDown,
  ChevronRight,
  Loader2,
  RefreshCcw,
} from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Console as ActionConsole,
  RHS as ActionRHS,
} from "../Projects/Editor/Actions/ActionRunner";
import { EditableField } from "./editable-field";

type CenterDisplay =
  | {
      type: "docs";
      nodeId: string;
    }
  | {
      type: typeof BUNDLE_TAB;
    }
  | {
      type: "action-console";
    };

type RunnerStep = {
  id: string;
  title: string;
  type: "extraction" | "rules" | "triggers" | "actions";
  displayType: "extraction" | "rules" | "actions";
  nodes: NodeData[];
  results: ResultOutput[];
  centerDisplay: CenterDisplay;
};

type Stepper = {
  taskId: string;
  hasBundle: boolean;
  steps: RunnerStep[];
  run: Run;
  citations: CitationSelect[];
  loading: {
    completed: number;
    total: number;
  } | null;
};

const BUNDLE_TAB = "__BUNDLE" as const;
type UseTaskStepperResult = {
  stepper: Stepper | null;
  bundleUrl: string | null;
};

type TaskResult = Awaited<ReturnType<typeof useTask>>["data"];

const useTaskStepper = (task: TaskResult | undefined): UseTaskStepperResult => {
  const [stepper, setStepper] = useState<Stepper | null>(null);
  const [bundleUrl, setBundleUrl] = useState<string | null>(null);

  useEffect(() => {
    const fn = async () => {
      if (stepper) return;
      if (!task) return;

      const run = task.runs[0].run; // For now, only support one run
      const documents = task.runs[0].documents;
      const citations = documents.flatMap((d) => d.citations);
      if (!run) return;

      /**
       * Four groups:
       * 0. Triggers
       * 1. Extraction steps
       * 2. Rule groups
       * 3. Action groups
       */

      const triggerNodeGroup = run.nodes.find(
        (n) => isGroupNode(n) && n.name === TRIGGER_GROUP_NODE_NAME,
      );
      const documentNodeGroup = run.nodes.find(
        (n) => isGroupNode(n) && n.name === DOCUMENT_GROUP_NODE_NAME,
      );
      const ruleGroupHolderNode = run.nodes.find(
        (n) => isGroupNode(n) && n.name === RULE_GROUP_HOLDER_NODE_NAME,
      );
      const actionNodeGroup = run.nodes.find(
        (n) => isGroupNode(n) && n.name === ACTION_GROUP_NODE_NAME,
      );

      const steps: RunnerStep[] = [];
      const urlsToMerge: string[] = [];

      for (const triggerNodeId of triggerNodeGroup?.dependencyIds ?? []) {
        const triggerNode = run.nodes.find((n) => n.id === triggerNodeId);
        // Only include email triggers for now
        if (!triggerNode || !isEmailNode(triggerNode)) continue;

        const results =
          run.results.filter(
            (r): r is EmailResult =>
              r.nodeId === triggerNode.id && isEmailResult(r),
          ) ?? [];

        // Only include triggers with results
        if (results.length === 0) continue;

        // Only include nodes with models
        if (!triggerNode.model) continue;

        steps.push({
          id: triggerNode.id,
          type: "triggers",
          displayType: "extraction",
          title: triggerNode.name,
          nodes: [triggerNode],
          results,
          centerDisplay: {
            type: "docs",
            nodeId: triggerNode.id,
          },
        });
      }

      for (const docNodeId of documentNodeGroup?.dependencyIds ?? []) {
        const docNode = run.nodes.find((n) => n.id === docNodeId);
        if (!docNode || !isDocumentNode(docNode)) continue;

        const docResults = run.results.filter(
          (r) => r.nodeId === docNode.id && isDocumentResult(r),
        ) as DocumentResult[] | undefined;
        const documentIds = docResults?.map((r) => r.documentId) ?? [];

        for (const docId of documentIds) {
          const document = documents.find((d) => d.id === docId);
          if (document?.signedUrl && document.fileType.includes("pdf")) {
            urlsToMerge.push(document.signedUrl);
          }
        }
        // Don't add steps without extraction fields
        if (
          docNode.model.type === "object" &&
          docNode.model.fields.length === 0
        ) {
          continue;
        }

        steps.push({
          id: docNodeId,
          type: "extraction",
          displayType: "extraction",
          title: docNode.name,
          nodes: [docNode],
          results: docResults ?? [],
          centerDisplay: {
            type: "docs",
            nodeId: docNode.id,
          },
        });
      }

      // TODO: make node-result pairs ??
      // Maybe just punt until Dag viewer

      for (const ruleGroupId of ruleGroupHolderNode?.dependencyIds ?? []) {
        const ruleGroupNode = run.nodes.find((n) => n.id === ruleGroupId);
        if (!ruleGroupNode) continue;

        const ruleNodes = run.nodes.filter((n) =>
          ruleGroupNode.dependencyIds.includes(n.id),
        );
        const results = run.results.filter((r) =>
          ruleNodes.map((n) => n.id).includes(r.nodeId),
        );

        steps.push({
          id: ruleGroupNode.id,
          type: "rules",
          displayType: "rules",
          title: ruleGroupNode.name,
          nodes: ruleNodes,
          results: results ?? [],
          centerDisplay: {
            type: BUNDLE_TAB,
          },
        });
      }

      for (const actionNodeId of actionNodeGroup?.dependencyIds ?? []) {
        const actionNode = run.nodes.find((n) => n.id === actionNodeId);
        if (!actionNode) continue;

        if (isSendEmailActionNode(actionNode)) {
          continue;
        }

        const actionResult = run.results.find(
          (r) => r.nodeId === actionNode.id,
        );

        steps.push({
          id: actionNode.id,
          type: "actions",
          displayType: "actions",
          title: actionNode.name,
          nodes: [actionNode],
          results: actionResult ? [actionResult] : [],
          centerDisplay: {
            type: "action-console",
          },
        });
      }

      const triggerPDFMerge = async () => {
        if (urlsToMerge.length === 0) return;

        const mergedPDF = await generateMergedPDF(urlsToMerge);
        setBundleUrl(mergedPDF?.url ?? null);
      };

      triggerPDFMerge();

      setStepper({
        run,
        taskId: task.id,
        hasBundle: urlsToMerge.length > 0,
        steps,
        citations,
        loading: null,
      });
    };

    fn();
  }, [task, stepper]);

  return { stepper, bundleUrl };
};

const TaskRunner = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: task } = useTask(id || "");

  const retryTaskMut = useRetryTask();
  const updateRunStatusMut = useUpdateRunStatus(task?.id ?? "");

  const [selectedSourceIds, setSelectedSourceIds] = useState<string[]>([]);

  const { stepper, bundleUrl } = useTaskStepper(task);
  const [stepId, setStepId] = useState<string | null>(null);
  const [tabDocNodeId, setTabDocNodeId] = useState<string>(BUNDLE_TAB);
  const [viewingResult, setViewingResult] = useState<
    DocumentResult | EmailResult | undefined | null
  >(null);

  const docNodeIds = useMemo(() => {
    const triggerNodes = stepper?.run.nodes.filter((n) => isEmailNode(n)) ?? [];
    const docGroupNode = stepper?.run.nodes.find(
      (n) => isGroupNode(n) && n.name === DOCUMENT_GROUP_NODE_NAME,
    );
    const triggerNodeIds = triggerNodes.map((n) => n.id);
    const docNodeIds = docGroupNode?.dependencyIds ?? [];

    return [...triggerNodeIds, ...docNodeIds];
  }, [stepper]);

  const document = useMemo(
    () =>
      task?.runs[0].documents.find((d) => d.id === viewingResult?.documentId),
    [task, viewingResult],
  );
  const docUrl = tabDocNodeId === BUNDLE_TAB ? bundleUrl : document?.signedUrl;

  const { currentStep, isLastStep } = useMemo(() => {
    const currentStepIdx = stepper?.steps.findIndex(
      (step) => step.id === stepId,
    );
    const currentStep = stepper?.steps.find((step) => step.id === stepId);
    const isLastStep = currentStepIdx === (stepper?.steps.length ?? 0) - 1;

    return {
      currentStepIdx,
      currentStep,
      isLastStep,
    };
  }, [stepper, stepId]);

  // For fetching convenience
  const resultsByNodeId = useMemo(() => {
    const results = task?.runs[0]?.run.results;
    if (results != null) return _.groupBy(results, "nodeId");

    return {};
  }, [task]);

  const handleSelectTab = useCallback(
    (tab: string) => {
      if (!stepper) return;

      setTabDocNodeId(tab);
      setSelectedSourceIds([]);

      // If a tab is selected, select the first document for the tab
      if (tab === BUNDLE_TAB) {
        return;
      }

      const inExtractionTab = currentStep?.displayType === "extraction";
      const newTabNode = stepper.run.nodes.find((n) => n.id === tab) as
        | DocumentNode
        | EmailNode
        | undefined;
      const newStep = stepper?.steps.find((s) => s.id === newTabNode?.id);

      if (!newTabNode) return;

      // if we're in an extraction tab and our new tab is also an extraction tab,
      // select that step
      if (inExtractionTab && newStep?.displayType === "extraction") {
        setStepId(newTabNode.id);
      }

      const results = resultsByNodeId[newTabNode.id]?.filter(
        (r) => isDocumentResult(r) || isEmailResult(r),
      );

      // Set first available result
      setViewingResult(results?.[0]);
    },
    [stepper, currentStep, resultsByNodeId],
  );

  const handleSelectStep = useCallback(
    (stepId: string) => {
      // If a step is selected, select the relevant tab

      const newStep = stepper?.steps.find((s) => s.id === stepId);
      if (!newStep) return;

      setStepId(stepId);
      setSelectedSourceIds([]);

      if (newStep.type === "triggers") {
        const emailNode = newStep?.nodes.find((n) => isEmailNode(n));
        const emailResult = resultsByNodeId[emailNode?.id ?? ""]?.[0];
        if (emailResult && isEmailResult(emailResult)) {
          setViewingResult(emailResult);
          setTabDocNodeId(emailNode?.id ?? "");
        }
      } else if (newStep.displayType === "rules") {
        if (bundleUrl) {
          // For rules steps, focus the bundle tab
          setTabDocNodeId(BUNDLE_TAB);
        }
      } else if (newStep.displayType === "extraction") {
        // For extraction steps, focus the relevant doc
        const docNode = newStep?.nodes[0];
        if (!docNode || !isDocumentNode(docNode)) {
          console.error("No node found for extraction step", newStep);
          return;
        }

        setTabDocNodeId(docNode.id);

        const relevantResults = resultsByNodeId[docNode?.id ?? ""];
        const firstResult = relevantResults?.[0] as DocumentResult | undefined;
        setViewingResult(firstResult ?? null);
      }
    },
    [stepper, resultsByNodeId, bundleUrl],
  );

  const handleSelectSourceIds = useCallback(
    (ids: string[]) => {
      if (!stepper) return;

      setSelectedSourceIds(ids);
      if (ids.length === 0) return;

      const citation = stepper.citations.find((h) => ids.includes(h.id));

      if (!citation) return;

      const results = stepper.run.results.filter(
        (r) => isDocumentResult(r) || isEmailResult(r),
      );

      const result = results.find((r) => r.documentId === citation.documentId);

      if (!result || !result.nodeId) {
        console.error("No result found for citation", citation);
        return;
      }

      setViewingResult(result);
      setTabDocNodeId(result.nodeId);
    },
    [stepper],
  );

  useEffect(() => {
    // On load,
    // If there is a stepper, and no step is selected, select the first step
    const firstStep = stepper?.steps[0];
    if (!stepId && firstStep) {
      handleSelectStep(firstStep.id);
    }
  }, [stepper, stepId, handleSelectStep]);

  const highlightedChunks = useMemo(() => {
    return (
      stepper?.citations
        ?.filter((chunk) => selectedSourceIds.includes(chunk.id))
        .map((chunk) => ({
          pageIndex: chunk.page - 1,
          left: chunk.left,
          top: chunk.top,
          width: chunk.width,
          height: chunk.height,
        })) ?? []
    );
  }, [stepper, selectedSourceIds]);

  const { results: relevantResults, docs: relevantDocs } = useMemo(() => {
    const results = (resultsByNodeId[tabDocNodeId ?? ""] ??
      []) as DocumentResult[];
    if (!results) return {};

    const docs =
      results.flatMap(
        (r) => task?.runs[0].documents.find((d) => d.id === r.documentId) ?? [],
      ) ?? [];

    return { results, docs };
  }, [resultsByNodeId, tabDocNodeId, task]);

  /**
   * Display is split vertically into thirds
   * 1. Steps
   *    - A list of steps for this Run
   * 2. Viewer
   *    - For extraction or rule steps shows a PDF
   *    - For action steps shows the action console
   * 3. Result Console
   *    - Shows the results of the steps
   *    - Results can often be edited, actions can be taken
   */
  return (
    <div className="flex flex-row h-[calc(100vh-theme(spacing.16))] overflow-auto w-full">
      <div className="flex w-full">
        <div className="w-3/12 ml-4 pt-4">
          <div className="flex flex-col">
            <div className="p-4 mb-6 flex items-center justify-between">
              <h1 className="text-2xl font-bold flex items-center">
                {task?.name}
              </h1>
              <div>
                {stepper?.run.status === RunStatus.FAILED && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      if (!task?.id) return;
                      retryTaskMut.mutateAsync(task.id);
                    }}
                    loading={retryTaskMut.isPending}
                  >
                    <RefreshCcw className="w-4 h-4 mr-2" />
                    Re-run
                  </Button>
                )}
              </div>
            </div>
            {Object.entries(
              stepper?.steps.reduce<Record<string, typeof stepper.steps>>(
                (groups, step) => {
                  const group = step.displayType || "Other";
                  groups[group] = [...(groups[group] || []), step];
                  return groups;
                },
                {},
              ) || {},
            ).map(([groupName, steps]) => (
              <div key={groupName} className="px-4 mb-7">
                <h3 className="text-lg font-bold mb-2">
                  {groupName === "extraction"
                    ? "Extraction"
                    : groupName === "rules"
                      ? "Rules"
                      : groupName === "triggers"
                        ? "Trigger"
                        : "Actions"}
                </h3>
                <div className="flex flex-col gap-1">
                  {steps.map((step) => (
                    <button
                      type="button"
                      key={step.id}
                      className={cn(
                        "cursor-pointer flex items-center space-x-2 w-full p-2 rounded-md font-medium",
                        {
                          "bg-blue-100 text-blue-700": stepId === step.id,
                          "hover:bg-gray-100": stepId !== step.id,
                        },
                      )}
                      onClick={() => {
                        handleSelectStep(step.id);
                      }}
                    >
                      {step.title}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="w-6/12 pt-4 h-full">
          {currentStep &&
            currentStep.centerDisplay.type !== "action-console" && (
              <>
                <Tabs
                  value={tabDocNodeId}
                  className="w-full"
                  onValueChange={(value) => {
                    const isInExtraction =
                      currentStep?.displayType === "extraction";
                    const selectedExtraction = stepper?.steps.find(
                      (s) =>
                        s.displayType === "extraction" &&
                        s.centerDisplay.type === "docs" &&
                        s.centerDisplay.nodeId === value,
                    );
                    if (isInExtraction && selectedExtraction) {
                      setStepId(selectedExtraction.id);
                    }

                    handleSelectTab(value);
                  }}
                >
                  {docNodeIds.length > 1 && (
                    <div className="w-full flex items-center justify-center">
                      <TabsList className="mb-4">
                        {stepper?.hasBundle && (
                          <TabsTrigger value={BUNDLE_TAB}>
                            All Documents
                          </TabsTrigger>
                        )}
                        {docNodeIds.map((tab) => {
                          const node = stepper?.run.nodes.find(
                            (n) => n.id === tab,
                          );
                          if (!node) return null;

                          const result = resultsByNodeId[node.id]?.find(
                            (r) => isDocumentResult(r) || isEmailResult(r),
                          );
                          if (!result) return null;

                          return (
                            <TabsTrigger key={tab} value={tab}>
                              {tab === BUNDLE_TAB ? "All Documents" : node.name}
                            </TabsTrigger>
                          );
                        })}
                      </TabsList>
                    </div>
                  )}
                </Tabs>
                {tabDocNodeId === BUNDLE_TAB && !docUrl && (
                  <div className="min-h-[640px] flex items-center justify-center">
                    <Loader2 className="w-12 text-gray-400 h-12 animate-spin" />
                  </div>
                )}
                {docUrl && (
                  <div className="overflow-auto h-[calc(100%-3.5rem)]">
                    <DocumentViewer
                      signedUrl={docUrl}
                      highlights={highlightedChunks}
                      documentType={
                        tabDocNodeId === BUNDLE_TAB
                          ? DocumentType.PDF
                          : viewingResult?.subType === "document"
                            ? (relevantDocs?.find(
                                (d) => d.id === viewingResult?.documentId,
                              )?.fileType as DocumentType)
                            : DocumentType.EMAIL
                      }
                    />
                  </div>
                )}
              </>
            )}
          {task &&
            stepper &&
            currentStep?.centerDisplay.type === "action-console" && (
              <ActionConsole
                actionNode={currentStep.nodes[0] as ActionNode}
                runId={stepper.run.id}
                taskId={stepper.taskId}
              />
            )}
        </div>
        <div className="grid grid-rows-[1fr,60px] w-4/12">
          <div className="overflow-auto">
            {currentStep && stepper && !stepper.loading && (
              <ResultConsole
                runId={stepper.run.id}
                citations={stepper.citations}
                taskId={stepper.taskId}
                currentStep={currentStep}
                resultsByNodeId={resultsByNodeId}
                onSelectSourceIds={handleSelectSourceIds}
                selectedResultId={viewingResult?.id ?? null}
                documents={relevantDocs ?? []}
                documentResults={relevantResults ?? []}
                onSelectDocResultId={(resultId) => {
                  setViewingResult(
                    relevantResults?.find((r) => r.id === resultId),
                  );
                }}
              />
            )}
            {stepper?.loading && (
              <div className="h-full w-full flex items-center justify-center">
                <div className="flex justify-center flex-col items-center bg-secondary border rounded-lg p-8 w-fit h-fit">
                  <p className="mb-2">
                    {stepper.loading.completed} of {stepper.loading.total} steps
                    completed
                  </p>
                  <Loader2 className="w-4 h-4 animate-spin" />
                </div>
              </div>
            )}
          </div>
          <div className="flex justify-end py-4 px-4 border-t w-full">
            {!isLastStep && (
              <Button
                onClick={() => {
                  if (!currentStep || !stepper) return;

                  const curStepIdx = stepper?.steps.findIndex(
                    (s) => s === currentStep,
                  );
                  if (curStepIdx == null || curStepIdx === -1) return;

                  const nextStepIdx = Math.min(
                    curStepIdx + 1,
                    stepper.steps.length - 1,
                  );
                  const nextStep = stepper?.steps[nextStepIdx];
                  if (!nextStep) return;

                  handleSelectStep(nextStep.id);
                }}
              >
                Next
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            )}
            {isLastStep && (
              <Button
                onClick={async () => {
                  await updateRunStatusMut.mutateAsync(RunStatus.COMPLETED);
                  navigate("/");
                }}
                className="bg-emerald-600 hover:bg-emerald-600/90 text-white"
                loading={updateRunStatusMut.isPending}
              >
                Complete
                <Check className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

type ResultConsoleProps = {
  taskId: string;
  runId: string;
  currentStep: RunnerStep;
  resultsByNodeId: Record<string, ResultOutput[]>;
  onSelectSourceIds: (sourceIds: string[]) => void;
  onSelectDocResultId: (resultId: string) => void;
  citations: CitationSelect[];
  selectedResultId: string | null;
  documentResults: DocumentResult[];
  documents: TaskDocumentSelect[];
};

const ResultConsole: React.FC<ResultConsoleProps> = ({
  taskId,
  runId,
  currentStep,
  resultsByNodeId,
  citations,
  onSelectSourceIds,
  documents,
  documentResults,
  onSelectDocResultId,
  selectedResultId,
}) => {
  const showingExtraction = currentStep.displayType === "extraction";

  return (
    <div className="w-full px-8 pt-4 rounded overflow-auto bg-white flex flex-col">
      <div className="my-4 flex items-center justify-between">
        <h2 className="text-2xl font-bold">{currentStep.title}</h2>
        {showingExtraction && documents.length > 1 && (
          <Select
            value={selectedResultId ?? ""}
            onValueChange={onSelectDocResultId}
          >
            <SelectTrigger className="w-fit max-w-96">
              <SelectValue placeholder="Select Document" />
            </SelectTrigger>
            <SelectContent>
              {documentResults.map((r) => {
                const d = documents.find((d) => d.id === r.documentId);
                return (
                  <SelectItem key={r.id} value={r.id}>
                    {d?.fileName ?? "Unknown Document"}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        )}
      </div>
      <div>
        {currentStep.displayType === "extraction" &&
          currentStep.nodes.map((node) => {
            const result = resultsByNodeId[node.id]?.find(
              (r) => r.id === selectedResultId,
            );

            if (isDocumentNode(node) || isEmailNode(node)) {
              const model = node.model ?? {
                type: "object",
                name: "Empty",
                fields: [],
              };

              return (
                <ExtractionResultDisplay
                  taskId={taskId}
                  key={result?.id}
                  model={model}
                  result={result as DocumentResult | EmailResult}
                  onSelectSourceIds={onSelectSourceIds}
                />
              );
            }
          })}
        {currentStep.displayType === "rules" && (
          <RuleGroups
            taskId={taskId}
            nodes={currentStep.nodes as ExtractionNode[]}
            resultsByNodeId={resultsByNodeId}
            citations={citations}
            onSelectSourceIds={onSelectSourceIds}
          />
        )}
        {currentStep.displayType === "actions" && (
          <ActionRHS
            actionNode={currentStep.nodes[0] as ActionNode}
            taskId={taskId}
            runId={runId}
          />
        )}
      </div>
    </div>
  );
};

type RuleDisplayZone = "Manual" | "Failed" | "Passed" | "Not Applicable";

type RuleType = "User Approval" | "Formula" | "Prompt";

type RuleNodeResult = {
  node: ExtractionNode;
  result?: RuleResult;
  displayZone: RuleDisplayZone;
  ruleType: RuleType;
};

type RuleResult = Omit<ExtractionResult, "data"> & {
  data?: {
    result?: string | null;
    reasoning?: string | null;
  };
  citations?: {
    result?: string[];
    reasoning?: string[];
  };
};

const RuleGroups: React.FC<{
  taskId: string;
  nodes: ExtractionNode[];
  resultsByNodeId: Record<string, ResultOutput[]>;
  citations: CitationSelect[];
  onSelectSourceIds: (sourceIds: string[]) => void;
}> = ({ taskId, nodes, resultsByNodeId, citations, onSelectSourceIds }) => {
  const sets = useMemo(() => {
    const manualSet: RuleNodeResult[] = [];
    const failedSet: RuleNodeResult[] = [];
    const passedSet: RuleNodeResult[] = [];
    const nonApplicableSet: RuleNodeResult[] = [];

    for (const node of nodes) {
      const backendResult = resultsByNodeId[node.id]?.[0] as RuleResult;

      const defaultResult: RuleResult = {
        type: "transform",
        subType: "extraction",
        nodeId: node.id,
        id: "",
        status: ResultStatus.COMPLETED,
        data: {
          result: null,
          reasoning: null,
        },
      };

      const r: RuleResult = backendResult ?? defaultResult;
      const ruleType: RuleType = node.model.userChoice
        ? "User Approval"
        : node.model.type === "formula"
          ? "Formula"
          : "Prompt";

      // Only push to manual set if both manual approval and no result yet
      if (node.model.userChoice && r.data?.result == null) {
        manualSet.push({
          node,
          result: r,
          displayZone: "Manual",
          ruleType,
        });
      } else if (r.data?.result === "Failed") {
        failedSet.push({
          node,
          result: r,
          displayZone: "Failed",
          ruleType,
        });
      } else if (r.data?.result === "Passed") {
        passedSet.push({
          node,
          result: r,
          displayZone: "Passed",
          ruleType,
        });
      } else if (r.data?.result === "Not Applicable") {
        nonApplicableSet.push({
          node,
          result: r,
          displayZone: "Not Applicable",
          ruleType,
        });
      }
    }

    return [
      { title: "Manual Approval Required", set: manualSet },
      { title: "Failed Rules", set: failedSet },
      { title: "Passed Rules", set: passedSet },
      { title: "Not Applicable Rules", set: nonApplicableSet },
    ];
  }, [nodes, resultsByNodeId]);

  return (
    <div className="flex flex-col gap-4">
      {sets.map((s) => (
        <div key={s.title}>
          <div className="font-medium mb-2">
            {s.title} ({s.set.length})
          </div>
          {s.set.length === 0 && (
            <div className="italic text-gray-500">No rules</div>
          )}
          <div className="flex flex-col gap-2 items-start">
            {s.set.map((s) => (
              <RuleItem
                key={s.result?.id ?? s.node.id}
                taskId={taskId}
                nodeResult={s}
                citations={citations}
                onSelectSourceIds={onSelectSourceIds}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const RuleItem: React.FC<{
  taskId: string;
  nodeResult: RuleNodeResult;
  citations: CitationSelect[];
  onSelectSourceIds: (sourceIds: string[]) => void;
}> = ({ taskId, nodeResult, citations, onSelectSourceIds }) => {
  const { node, result, ruleType } = nodeResult;
  const updateResult = useUpdateResult(taskId);

  const resultField =
    node.model.type === "object"
      ? node.model.fields.find((f) => f.name === "result")
      : null;
  const resultValue = result?.data?.result;

  const description =
    node.model.type === "formula"
      ? node.model.formula
      : resultField?.description;

  const sourceIds = [
    ...new Set([
      ...(result?.citations?.result ?? []),
      ...(result?.citations?.reasoning ?? []),
    ]),
  ];
  const reasoningValue = result?.data?.reasoning;

  return (
    <div className="border shadow-sm py-4 px-6 rounded flex flex-col gap-2 max-w-[640px] w-full">
      <div className="font-medium flex items-center justify-between">
        {node.name}
        <div className="flex items-center gap-2">
          <Tooltip>
            <TooltipTrigger>
              <QuestionMarkCircledIcon className="w-4 h-4 text-gray-500" />
            </TooltipTrigger>
            <TooltipContent className="max-w-[480px]">
              {tiptapToPlaintext(description)}
            </TooltipContent>
          </Tooltip>
          <div
            className={cn(
              "text-xs px-3 py-1.5 text-center rounded-full h-fit",
              {
                "bg-emerald-300 text-emerald-950": ruleType === "User Approval",
                "bg-orange-300 text-orange-950": ruleType === "Formula",
                "bg-sky-300 text-sky-950": ruleType === "Prompt",
              },
            )}
          >
            {ruleType}
          </div>
        </div>
      </div>
      {!resultValue && (
        <p className="text-gray-700">{tiptapToPlaintext(description)}</p>
      )}
      <RuleResultButtons
        value={resultValue}
        onChange={(value) => {
          if (!result) return;

          const newResult = produce(result, (draft) => {
            if (draft.data == null) {
              draft.data = { result: null, reasoning: null };
            }

            if (draft?.data != null) {
              draft.data.result = value;
            }
          });

          updateResult.mutateAsync({
            resultId: result.id,
            update: newResult,
          });
        }}
        className="flex gap-2"
      />
      <p className="break-words">{resultValue && reasoningValue}</p>
      <div className="flex gap-2">
        {sourceIds?.slice(0, 5).map((id, i) => (
          <Tooltip key={id}>
            <TooltipTrigger asChild>
              <div
                className="hover:bg-secondary w-fit border text-xs cursor-pointer px-2 py-1 rounded"
                onClick={() => onSelectSourceIds?.([id])}
              >
                {i + 1}
              </div>
            </TooltipTrigger>
            <TooltipContent className="max-w-48">
              {citations.find((c) => c.id === id)?.text}
            </TooltipContent>
          </Tooltip>
        ))}
      </div>
    </div>
  );
};

const RuleResultButtons = ({
  value,
  onChange,
  className,
}: {
  value?: string | null;
  onChange: (value: string) => void;
  className?: string;
}) => {
  return (
    <div className={cn("", className)}>
      <Button
        variant={"outline"}
        className={cn(
          "px-4 py-2 rounded",
          value === "Passed" && "bg-green-500 text-white",
        )}
        onClick={() => onChange("Passed")}
      >
        Accept
      </Button>
      <Button
        variant={"outline"}
        className={cn(
          "px-4 py-2 rounded",
          value === "Failed" && "bg-red-500 text-white",
        )}
        onClick={() => onChange("Failed")}
      >
        Reject
      </Button>
      <Button
        variant={"outline"}
        className={cn(
          "py-2 rounded",
          value === "Not Applicable" && "bg-yellow-500 text-white",
        )}
        onClick={() => onChange("Not Applicable")}
      >
        N/A
      </Button>
    </div>
  );
};

const ExtractionResultDisplay: React.FC<{
  taskId: string;
  model: Model;
  result?: DocumentResult | EmailResult;
  onSelectSourceIds: (sourceIds: string[]) => void;
}> = ({ taskId, model, result, onSelectSourceIds }) => {
  const [focusedKey, setFocusedKey] = useState<string | null>(null);

  const updateResult = useUpdateResult(taskId);

  const handleUpdateResult = useCallback(
    (key: string, value: unknown) => {
      if (!result) return;
      const newResult = structuredClone(result);
      _.set(newResult, `data.${key}`, value);

      if (newResult && result?.id) {
        updateResult.mutateAsync({
          resultId: result.id,
          update: newResult,
        });
      }
    },
    [result, updateResult],
  );

  const renderObj = (
    model: ObjectModel,
    res: ModelResult = {},
    citations: ModelResult = {},
    rootKey = "",
  ) => {
    if (model.type !== "object") {
      console.error("Received non-object model", model);
      return null;
    }

    return (
      <div className="flex flex-col gap-2">
        {model.fields.map((field, i) => {
          if (field.type === "array") {
            const items: ModelResult[] = res[field.name] ?? [];
            const itemCitations = citations[field.name] ?? [];

            return (
              <ExpandableSection
                name={field.name}
                count={items.length}
                key={field.name}
              >
                <div className="mt-4 flex flex-col gap-2">
                  {items.map((item, j) => {
                    const fieldNames = Object.keys(item);
                    const nameFieldKey = fieldNames.find((key) =>
                      key.toLowerCase().includes("name"),
                    );
                    const name = nameFieldKey ? item[nameFieldKey] : j;
                    const editKey = rootKey
                      ? `${rootKey}.${j}.${field.name}`
                      : `${field.name}.${j}`;

                    // Only supporting object models inside lists for now
                    if (field.model.type !== "object") {
                      return null;
                    }

                    return (
                      <ExpandableSection
                        key={item.name}
                        name={name}
                        onMouseEnter={() => {
                          const childCitations = Object.values(
                            itemCitations[j] ?? {},
                          ).flat();
                          onSelectSourceIds(childCitations as string[]);
                        }}
                      >
                        <div className="mt-4">
                          {renderObj(
                            field.model,
                            item,
                            itemCitations[j],
                            editKey,
                          )}
                        </div>
                      </ExpandableSection>
                    );
                  })}
                </div>
              </ExpandableSection>
            );
          }

          const value = res[field.name];
          const sourceIds = citations[field.name] ?? [];
          const editKey = rootKey ? `${rootKey}.${field.name}` : field.name;

          return (
            <div key={`${field.name}-${i}`} className="">
              <Field
                name={field.name}
                value={value}
                editing={focusedKey === editKey}
                onClick={() => {
                  onSelectSourceIds?.(sourceIds);
                  setFocusedKey(editKey);
                }}
                onMouseEnter={() => {
                  if (!focusedKey) {
                    onSelectSourceIds?.(sourceIds);
                  }
                }}
                onChange={(newValue) => {
                  handleUpdateResult(editKey, newValue);
                  setFocusedKey(null);
                }}
              />
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div>
      {renderObj(
        model as ObjectModel,
        result?.data ?? {},
        result?.citations ?? {},
      )}
    </div>
  );
};

const Field = ({
  name,
  value,
  editing,
  onClick,
  onMouseEnter,
  onChange,
}: {
  name: string;
  // biome-ignore lint/suspicious/noExplicitAny: required any
  value: any;
  editing: boolean;
  onClick: () => void;
  onMouseEnter: () => void;
  // biome-ignore lint/suspicious/noExplicitAny: required any
  onChange: (newValue: any) => void;
}) => {
  return (
    <div
      className={cn("px-3 border py-2 rounded cursor-pointer", {
        "hover:bg-secondary border-transparent": !editing,
        "bg-secondary": editing,
      })}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          onClick();
        }
      }}
    >
      <div className="font-semibold text-gray-700 mb-1.5">{name}</div>
      <EditableField
        editing={editing}
        value={value}
        onChange={(newValue) => onChange?.(newValue)}
      />
    </div>
  );
};

const ExpandableSection = ({
  name,
  children,
  count,
  onMouseEnter,
}: {
  name?: string;
  children: React.ReactNode;
  count?: number;
  onMouseEnter?: () => void;
}) => {
  const [expanded, setExpanded] = useState(false);

  const handleClick = () => {
    setExpanded((prev) => !prev);
  };

  return (
    <div className="border rounded p-2" onMouseEnter={onMouseEnter}>
      <div
        className={cn(
          "p-2 -m-1 cursor-pointer flex items-center justify-between font-medium hover:bg-secondary",
          {},
        )}
        onClick={handleClick}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            handleClick();
          }
        }}
      >
        <p className="font-medium">
          {name}
          {count != null && (
            <span className="ml-3 text-xs text-gray-500">{count} items</span>
          )}
        </p>
        <div>
          {expanded ? (
            <ChevronDown className="w-4 h-4" />
          ) : (
            <ChevronRight className="w-4 h-4" />
          )}
        </div>
      </div>
      {expanded && children}
    </div>
  );
};

export default TaskRunner;
